import React, { useCallback, useEffect, useState } from 'react'
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { GppMaybe, RemoveModerator, CheckCircle } from '@mui/icons-material'
import { useSnackbar } from 'notistack'
import { PageHeader } from '@/components/common/PageHeader'
import {
  usuarioService,
  DiagnosticoOperador,
  PortadorOperador,
} from '@/services/usuarioService'

/**
 * Saneamento do OPERADOR concedido indevidamente (defeito de defaultRoles do realm,
 * corrigido em 03/08 para contas novas — esta tela trata o passivo).
 *
 * **A fronteira da tela é a mesma do backend.** Quem veio do funil de consultor tem
 * evidência (a candidatura vinculada à conta) e é revogável em lote. Quem não veio —
 * contas criadas por admin: operadores legítimos, gerentes — **nunca entra no lote**:
 * cada revogação é um clique consciente, com confirmação. Revogar em massa tiraria acesso
 * de operador legítimo, que é o erro que a pergunta original queria evitar.
 */

const SaneamentoOperadorPage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar()
  const [diagnostico, setDiagnostico] = useState<DiagnosticoOperador | null>(null)
  const [carregando, setCarregando] = useState(true)
  const [erro, setErro] = useState<string | null>(null)
  const [confirmandoLote, setConfirmandoLote] = useState(false)
  const [revogandoUm, setRevogandoUm] = useState<PortadorOperador | null>(null)
  const [executando, setExecutando] = useState(false)

  const carregar = useCallback(async () => {
    setCarregando(true)
    setErro(null)
    try {
      setDiagnostico(await usuarioService.saneamentoOperador())
    } catch {
      setErro('Não foi possível carregar o diagnóstico.')
    } finally {
      setCarregando(false)
    }
  }, [])

  useEffect(() => {
    void carregar()
  }, [carregar])

  const revogarLote = async () => {
    setExecutando(true)
    try {
      const revogados = await usuarioService.revogarOperadorDoFunil()
      enqueueSnackbar(
        `OPERADOR revogado de ${revogados.length} consultor(es). Vale a partir do próximo login de cada um.`,
        { variant: 'success' }
      )
      setConfirmandoLote(false)
      await carregar()
    } catch {
      enqueueSnackbar('Falha ao revogar em lote.', { variant: 'error' })
    } finally {
      setExecutando(false)
    }
  }

  const revogarUm = async () => {
    if (!revogandoUm) return
    setExecutando(true)
    try {
      await usuarioService.revogarOperadorIndividual(revogandoUm.usuarioId)
      enqueueSnackbar(`OPERADOR revogado de ${revogandoUm.email ?? revogandoUm.usuarioId}.`, {
        variant: 'success',
      })
      setRevogandoUm(null)
      await carregar()
    } catch {
      enqueueSnackbar('Falha ao revogar.', { variant: 'error' })
    } finally {
      setExecutando(false)
    }
  }

  const linha = (p: PortadorOperador, acao: React.ReactNode) => (
    <TableRow key={p.usuarioId} hover>
      <TableCell>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {p.nome || '—'}
        </Typography>
        <Typography variant="caption" color="textSecondary">
          {p.email ?? p.usuarioId}
        </Typography>
      </TableCell>
      <TableCell>
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
          {p.outrosPapeis.map((r) => (
            <Chip key={r} size="small" label={r} variant="outlined" />
          ))}
          {!p.habilitado && <Chip size="small" label="Desabilitado" color="default" />}
        </Stack>
      </TableCell>
      <TableCell align="right">{acao}</TableCell>
    </TableRow>
  )

  if (carregando) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress aria-label="Carregando diagnóstico" />
      </Box>
    )
  }

  const funil = diagnostico?.consultoresDoFunil ?? []
  const semOrigem = diagnostico?.semOrigemNoFunil ?? []
  const limpo = funil.length === 0 && semOrigem.length === 0

  return (
    <Box>
      <PageHeader
        title="Saneamento do papel OPERADOR"
        subtitle="Contas que receberam OPERADOR pelo defeito do realm (corrigido em 03/08 para contas novas)"
      />

      {erro && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button size="small" color="inherit" onClick={() => void carregar()}>
              Tentar de novo
            </Button>
          }
        >
          {erro}
        </Alert>
      )}

      {limpo && !erro && (
        <Card sx={{ p: 4, textAlign: 'center' }}>
          <CheckCircle sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Nenhuma conta com OPERADOR pendente de revisão
          </Typography>
          <Typography variant="body2" color="textSecondary">
            O passivo do defeito de defaultRoles foi saneado.
          </Typography>
        </Card>
      )}

      {funil.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Consultores do funil com OPERADOR — {funil.length}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Entraram pela candidatura de consultor; o OPERADOR nessas contas só pode
                ser resto do defeito. Revogáveis em lote.
              </Typography>
            </Box>
            <Button
              variant="contained"
              color="error"
              startIcon={<RemoveModerator />}
              onClick={() => setConfirmandoLote(true)}
            >
              Revogar de todos ({funil.length})
            </Button>
          </Box>
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Conta</TableCell>
                  <TableCell>Outros papéis</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>{funil.map((p) => linha(p, null))}</TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      {semOrigem.length > 0 && (
        <Card>
          <Box sx={{ p: 2 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <GppMaybe color="warning" />
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Sem origem no funil — {semOrigem.length}
              </Typography>
            </Stack>
            <Typography variant="body2" color="textSecondary">
              Contas criadas por admin: aqui podem estar os operadores legítimos.{' '}
              <strong>Nada é revogado em lote</strong> — confirme conta a conta quem não
              deve ter o papel.
            </Typography>
          </Box>
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Conta</TableCell>
                  <TableCell>Outros papéis</TableCell>
                  <TableCell align="right">Ação</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {semOrigem.map((p) =>
                  linha(
                    p,
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      onClick={() => setRevogandoUm(p)}
                    >
                      Revogar
                    </Button>
                  )
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      <Dialog open={confirmandoLote} onClose={() => setConfirmandoLote(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Revogar OPERADOR de {funil.length} consultor(es)?</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 1 }}>
            <AlertTitle>O que acontece</AlertTitle>
            Cada conta perde o papel OPERADOR e mantém os demais papéis. A revogação vale a
            partir do <strong>próximo login</strong> de cada um — quem estiver logado mantém
            o token atual até expirar. Tudo fica registrado na auditoria.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmandoLote(false)}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={() => void revogarLote()} disabled={executando}>
            {executando ? 'Revogando…' : 'Revogar de todos'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={revogandoUm !== null} onClose={() => setRevogandoUm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Revogar OPERADOR desta conta?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            <strong>{revogandoUm?.nome || revogandoUm?.email}</strong> não veio do funil de
            consultor — pode ser um operador legítimo. Confirme que esta conta não deve ter
            o papel antes de revogar.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevogandoUm(null)}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={() => void revogarUm()} disabled={executando}>
            {executando ? 'Revogando…' : 'Confirmar revogação'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default SaneamentoOperadorPage
